import clsx from 'clsx';
import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { Title } from '../Title/Title';

import styles from './Modal.module.scss';

type BaseModalProps = {
  className?: string;
  showModal: boolean;
  onClose: () => void;
  overlayClass?: string;
};

type ConfirmModalProps = {
  view: 'confirm';
  data: {
    title: string | React.ReactNode;
    text: string;
    confirmButton: {
      text: string;
      onClick: () => void;
    };
    cancelButton: {
      text: string;
      onClick: () => void;
    };
  };
};

type AlertModalProps = {
  view: 'alert';
  data: {
    title: string | React.ReactNode;
    text: string;
    okButton: {
      text: string;
      onClick: () => void;
    };
  };
};

type ActionModalProps = Pick<AlertModalProps, 'data'> & {
  view: 'action';
};

type FullscreenModalProps = {
  view: 'fullscreen';
  data?: null;
};

type InfoModalProps = {
  view: 'info';
  data?: {
    title?: string;
  };
};

type ModalPropsNew = BaseModalProps &
  (ConfirmModalProps | AlertModalProps | FullscreenModalProps | InfoModalProps | ActionModalProps);

export const Modal: React.FC<React.PropsWithChildren<ModalPropsNew>> = ({
  className,
  showModal,
  onClose,
  overlayClass,
  view,
  children,
  data,
}) => {
  const htmlElement = useRef(document.getElementsByTagName('html')[0]);

  const handleEscape = useCallback(
    (event: any) => {
      if (event.keyCode === 27) onClose();
    },
    [onClose],
  );

  useEffect(() => {
    const html = htmlElement.current;
    if (htmlElement.current && showModal) {
      htmlElement.current.setAttribute('style', 'overflow:hidden');
      window.addEventListener('keydown', handleEscape);
    }

    return () => {
      if (showModal) {
        html.removeAttribute('style');
      }
      window.removeEventListener('keydown', handleEscape);
    };
  }, [showModal, handleEscape]);

  if (!showModal) return null;

  return createPortal(
    <div
      className={styles.animatedDiv}
      data-modal-root="true"
    >
      <div
        className={clsx(styles.overlay, overlayClass)}
        data-modal-overlay="true"
      >
        <div
          className={clsx(styles.box, styles[view], className)}
          data-modal-content="true"
        >
          {view === 'confirm' && (
            <>
              <Title
                htmlElement="h3"
                className={styles.title}
              >
                {data.title}
              </Title>
              <div className={styles.text}>{data.text}</div>
              {children}
              <div className={styles.buttons}>
                <Button
                  view="secondary"
                  onClick={data.cancelButton.onClick}
                >
                  {data.cancelButton.text}
                </Button>
                <Button onClick={data.confirmButton.onClick}>{data.confirmButton.text}</Button>
              </div>
            </>
          )}

          {view === 'alert' && (
            <>
              <Icon
                name="close"
                className={clsx(styles.btnClose)}
                onClick={onClose}
              />
              <Title
                htmlElement="h3"
                className={styles.title}
              >
                {data.title}
              </Title>
              <div className={styles.text}>{data.text}</div>
              {children}
              <div className={styles.buttons}>
                <Button onClick={data.okButton.onClick}>{data.okButton.text}</Button>
              </div>
            </>
          )}

          {view === 'action' && (
            <>
              <Title
                htmlElement="h3"
                className={styles.title}
              >
                {data.title}
              </Title>
              <div className={styles.text}>{data.text}</div>
              {children}
              {data?.okButton && (
                <div className={styles.buttons}>
                  <Button onClick={data.okButton.onClick}>{data.okButton.text}</Button>
                </div>
              )}
            </>
          )}

          {view === 'info' && (
            <>
              <Icon
                name="close"
                className={clsx(styles.btnClose)}
                onClick={onClose}
              />
              {data?.title && (
                <Title
                  htmlElement="h3"
                  className={styles.title}
                >
                  {data.title}
                </Title>
              )}
              {children}
            </>
          )}

          {view === 'fullscreen' && children}
        </div>
        {view === 'fullscreen' && (
          <Icon
            name="close"
            className={clsx(styles.btnClose)}
            onClick={onClose}
          />
        )}
      </div>
    </div>,
    document.body,
  );
};
